import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './DiscordDashboard.css';
import '../Zitadel-Auth/Auth.css';

interface GuildSettings {
  guild_id: string;
  guild_name?: string;
  command_channels: string[];
  spawn_updates_enabled: boolean;
  spawn_update_channels: string[];
  created_at: string;
  updated_at: string;
}

interface DiscordGuild {
  id: string;
  name: string;
  icon: string | null;
  owner: boolean;
  permissions: string;
}

const DiscordDashboard: React.FC = () => {
  // Dashboard doesn't use main auth system - only Discord auth
  const navigate = useNavigate();
  const [discordUser, setDiscordUser] = useState<{username: string, id: string} | null>(null);
  const [guilds, setGuilds] = useState<DiscordGuild[]>([]);
  const [guildSettings, setGuildSettings] = useState<{[key: string]: GuildSettings}>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check if user is logged in with Discord
  const hasDiscordToken = localStorage.getItem('discordToken');

  useEffect(() => {
    // Check for Discord token in URL parameters (from Discord OAuth callback)
    const urlParams = new URLSearchParams(window.location.search);
    const discordTokenFromUrl = urlParams.get('token');

    if (discordTokenFromUrl) {
      // Store Discord token separately from main auth token
      localStorage.setItem('discordToken', discordTokenFromUrl);
      // Remove token from URL for security
      window.history.replaceState({}, document.title, window.location.pathname);

      // Extract Discord user info and fetch guilds
      extractDiscordUserFromToken();
      setTimeout(() => {
        fetchDiscordGuilds();
      }, 200);
      return;
    }

    // Check if we have a Discord token stored
    const storedDiscordToken = localStorage.getItem('discordToken');
    if (storedDiscordToken) {
      // Extract Discord user info from token and fetch guilds
      extractDiscordUserFromToken();
      fetchDiscordGuilds();
    }
    // If no token, show login UI (don't auto-redirect)
  }, [navigate]);

  const handleAuthorizeDashboard = () => {
    window.location.href = `${import.meta.env.VITE_DISCORD_DASHBOARD_AUTH_URL || '/api/auth/discord/dashboard'}`;
  };

  const handleDiscordLogout = () => {
    localStorage.removeItem('discordToken');
    setDiscordUser(null);
    setGuilds([]);
    setError(null);
    setLoading(false);
    // Stay on dashboard - will show login screen
  };

  const extractDiscordUserFromToken = () => {
    const discordToken = localStorage.getItem('discordToken');
    if (discordToken) {
      try {
        // Decode JWT token to get Discord user info (client-side decoding for display only)
        const payload = JSON.parse(atob(discordToken.split('.')[1]));
        if (payload.discord_username && payload.discord_id) {
          setDiscordUser({
            username: payload.discord_username,
            id: payload.discord_id
          });
        }
      } catch (error) {
        console.error('Failed to decode Discord token:', error);
      }
    }
  };

  const fetchDiscordGuilds = async () => {
    try {
      setLoading(true);

      const discordToken = localStorage.getItem('discordToken');
      if (!discordToken) {
        handleAuthorizeDashboard();
        return;
      }

      // Fetch user's Discord guilds where bot is present
      const guildsResponse = await fetch('/api/discord/guilds/with-bot', {
        headers: {
          'Authorization': `Bearer ${discordToken}`
        }
      });

      if (!guildsResponse.ok) {
        const errorData = await guildsResponse.json();

        // Check if Discord token is invalid/expired
        if (guildsResponse.status === 401 || errorData.needsDiscordAuth) {
          // Clear invalid token and re-authenticate
          localStorage.removeItem('discordToken');
          handleAuthorizeDashboard();
          return;
        }

        throw new Error(errorData.message || 'Failed to fetch Discord guilds');
      }

      const response = await guildsResponse.json();
      const userGuilds: DiscordGuild[] = response.guilds || [];

      setGuilds(userGuilds);

      // Fetch bot settings for each guild (we know bot is present in all these guilds)
      const settingsPromises = userGuilds.map(async (guild) => {
        try {
          const response = await fetch(`/api/discord/settings/${guild.id}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('discordToken')}`
            }
          });

          if (response.ok) {
            const settings = await response.json();
            return { guildId: guild.id, settings };
          } else if (response.status === 404) {
            // Settings don't exist yet but bot should be present, create default settings
            const defaultSettings: GuildSettings = {
              guild_id: guild.id,
              guild_name: guild.name,
              command_channels: [],
              spawn_updates_enabled: false,
              spawn_update_channels: [],
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            };
            return { guildId: guild.id, settings: defaultSettings };
          }
        } catch (error) {
          console.error(`Failed to fetch settings for guild ${guild.id}:`, error);
        }
        return null;
      });

      const settingsResults = await Promise.all(settingsPromises);
      const settingsMap: {[key: string]: GuildSettings} = {};

      settingsResults.forEach(result => {
        if (result) {
          settingsMap[result.guildId] = result.settings;
        }
      });

      setGuildSettings(settingsMap);
    } catch (error) {
      console.error('Error fetching Discord data:', error);
      setError('Failed to load Discord server information');
    } finally {
      setLoading(false);
    }
  };



  // If no Discord token, show login screen
  if (!hasDiscordToken) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h1>🤖 Discord Bot Dashboard</h1>
            <p>Manage PRO Market History Bot settings for your Discord servers</p>
          </div>
          <div className="auth-form">
            <button
              onClick={handleAuthorizeDashboard}
              className="auth-button discord-button"
            >
              <span className="button-icon">🎮</span>
              Continue with Discord
            </button>
            <div className="auth-footer">
              <button
                onClick={() => navigate('/')}
                className="link-button"
              >
                ← Back to Main Site
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="discord-dashboard-container">
        <div className="discord-dashboard">
          <div className="dashboard-header">
            <h1>🤖 Discord Bot Dashboard</h1>
            <button onClick={() => navigate('/')} className="back-button">← Back to Site</button>
          </div>
          <div className="loading">Loading your Discord servers...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="discord-dashboard-container">
        <div className="discord-dashboard">
          <div className="dashboard-header">
            <h1>🤖 Discord Bot Dashboard</h1>
            <button onClick={() => navigate('/')} className="back-button">← Back to Site</button>
          </div>
          <div className="error">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="discord-dashboard-container">
      <div className="discord-dashboard">
      <div className="dashboard-header">
        <h1>🤖 Discord Bot Dashboard</h1>
        <div className="header-actions">
          <span className="user-info">👋 {discordUser?.username || 'Discord User'}</span>
          <button onClick={() => navigate('/')} className="back-button">← Back to Site</button>
          <button onClick={handleDiscordLogout} className="logout-button">Logout</button>
        </div>
      </div>

      <div className="dashboard-content">
        <div className="intro-section">
          <h2>Manage Your Discord Servers</h2>
          <p>Configure PRO Market History Bot settings for servers where you have administrative permissions.</p>
        </div>

        {guilds.length === 0 ? (
          <div className="no-guilds">
            <h3>No Manageable Servers Found</h3>
            <p>You don't have administrative permissions in any servers with the PRO Market History Bot.</p>
          </div>
        ) : (
          <div className="guilds-grid">
            {guilds.map(guild => {
              const settings = guildSettings[guild.id];
              return (
                <div key={guild.id} className="guild-card">
                  <div className="guild-header">
                    <div className="guild-info">
                      {guild.icon && (
                        <img
                          src={`https://cdn.discordapp.com/icons/${guild.id}/${guild.icon}.png`}
                          alt={`${guild.name} icon`}
                          className="guild-icon"
                        />
                      )}
                      <div>
                        <h3>{guild.name}</h3>
                        <span className="guild-id">ID: {guild.id}</span>
                      </div>
                    </div>
                    {guild.owner && <span className="owner-badge">👑 Owner</span>}
                  </div>

                  <div className="guild-actions">
                    <button
                      onClick={() => navigate(`/dashboard/server/${guild.id}`)}
                      className="settings-button"
                    >
                      ⚙️ Server Settings
                    </button>
                    {settings && (
                      <div className="settings-preview">
                        <span className="settings-status">
                          {settings.spawn_updates_enabled ? '🔄 Updates: ON' : '🔄 Updates: OFF'}
                        </span>
                        <span className="settings-status">
                          📝 Commands: {settings.command_channels?.length ? `${settings.command_channels.length} channels` : 'All channels'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
    </div>
  );
};

export default DiscordDashboard;
